import React, { useState } from 'react';
import { 
  Settings as SettingsIcon, 
  Save, 
  Globe, 
  Mail, 
  Database, 
  Shield, 
  Bell, 
  Palette,
  RefreshCw,
  Users,
  Upload,
  MapPin,
  Phone,
  Clock,
  MessageCircle,
  Share2,
  AlertTriangle,
  BarChart3,
  X,
  Maximize2,
  Minimize2
} from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useActivityLogger } from '../../hooks/useActivityLogger';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useExpandable } from '../../hooks/useExpandable';
import SystemActivities from '../../components/SystemActivities';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { getAssetUrl } from '../../utils/assetUrl';
import { useTranslation } from 'react-i18next';

interface SystemSettings {
  site: {
    name: string;
    description: string;
    logo: string;
    favicon: string;
    language: string;
    timezone: string;
    contact?: { email: string; phone: string; address: string; businessHours: string };
    geo?: { latitude: string; longitude: string; showLocation?: boolean };
  };
  email: {
    provider: string;
    sendgridApiKey: string;
    mailersendApiKey: string;
    smtpHost: string;
    smtpPort: number;
    smtpUser: string;
    smtpPassword: string;
    smtpSecure: boolean;
    fromEmail: string;
    fromName: string;
  };
  database: {
    backupEnabled: boolean;
    backupFrequency: string;
    retentionDays: number;
  };
  security: {
    sessionTimeout: number;
    maxLoginAttempts: number;
    passwordMinLength: number;
    twoFactorEnabled: boolean;
  };
  notifications: {
    emailNotifications: boolean;
    adminNotifications: boolean;
    userNotifications: boolean;
  };
  social?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
    youtube?: string;
    tiktok?: string;
    whatsapp?: string;
  };
  appearance: {
    primaryColor: string;
    secondaryColor: string;
    theme: string;
    customCSS: string;
  };
  team: {
    showTeamSection: boolean;
    hideWhenEmpty: boolean;
  };
  analytics?: {
    enabled: boolean;
    googleAnalyticsId: string;
    metaPixelId: string;
  };
  chatWidget?: {
    enabled: boolean;
    webhookUrl: string;
    typingLabel?: string;
    title?: string;
  };
  paymentOverdueEmail?: {
    enabled: boolean;
    client: { subject: string; body: string };
    admin: { subject: string; body: string };
  };
  socialMediaApi?: {
    pixabay?: { apiKey?: string };
    providers?: {
      anthropic?: { apiKey?: string };
      openai?: { apiKey?: string };
      gemini?: { apiKey?: string };
    };
    llm?: { provider?: string; model?: string };
    imageGen?: { provider?: string; model?: string };
    meta?: {
      appId?: string;
      appSecret?: string;
      pageAccessToken?: string;
      instagramBusinessAccountId?: string;
      facebookPageId?: string;
    };
  };
}

const AdminSettings: React.FC = () => {
  const { t } = useTranslation();
  const { settings, updateSettings, updateSettingsOnly } = useSettings();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('site');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const notifications = useNotifications();
  const { logSettingsActivity } = useActivityLogger();
  const { user } = useAuth();

  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFALoading, setTwoFALoading] = useState(true);
  const [twoFASetupData, setTwoFASetupData] = useState<{ qrCodeDataUrl: string; secret: string } | null>(null);
  const [twoFAConfirmCode, setTwoFAConfirmCode] = useState('');
  const [twoFARecoveryCodes, setTwoFARecoveryCodes] = useState<string[] | null>(null);
  const [twoFASaving, setTwoFASaving] = useState(false);
  const [showDisableTwoFAModal, setShowDisableTwoFAModal] = useState(false);
  const [disableTwoFAPassword, setDisableTwoFAPassword] = useState('');
  const { isExpanded: isTwoFAModalExpanded, setIsExpanded: setIsTwoFAModalExpanded, toggleExpanded: toggleTwoFAModalExpanded } = useExpandable();
  useEscapeKey(() => { setShowDisableTwoFAModal(false); setDisableTwoFAPassword(''); setIsTwoFAModalExpanded(false); }, showDisableTwoFAModal);

  React.useEffect(() => {
    const fetchTwoFAStatus = async () => {
      try {
        const res = await axios.get('/2fa/status');
        setTwoFAEnabled(res.data.enabled);
      } catch {
        /* ignore */
      } finally {
        setTwoFALoading(false);
      }
    };
    fetchTwoFAStatus();
  }, []);

  const startTwoFASetup = async () => {
    try {
      setTwoFASaving(true);
      const res = await axios.post('/2fa/setup');
      setTwoFASetupData(res.data);
      setTwoFAConfirmCode('');
    } catch (err: any) {
      notifications.showError('Erro', err.response?.data?.message || 'Erro ao iniciar configuração do 2FA');
    } finally {
      setTwoFASaving(false);
    }
  };

  const confirmTwoFASetup = async () => {
    try {
      setTwoFASaving(true);
      const res = await axios.post('/2fa/confirm', { code: twoFAConfirmCode });
      setTwoFARecoveryCodes(res.data.recoveryCodes);
      setTwoFASetupData(null);
      setTwoFAEnabled(true);
      notifications.success.settingsUpdated();
    } catch (err: any) {
      notifications.showError('Erro', err.response?.data?.message || 'Código inválido');
    } finally {
      setTwoFASaving(false);
    }
  };

  const disableTwoFA = async () => {
    try {
      setTwoFASaving(true);
      await axios.post('/2fa/disable', { password: disableTwoFAPassword });
      setTwoFAEnabled(false);
      setShowDisableTwoFAModal(false);
      setDisableTwoFAPassword('');
      setIsTwoFAModalExpanded(false);
      notifications.success.settingsUpdated();
    } catch (err: any) {
      notifications.showError('Erro', err.response?.data?.message || 'Senha incorreta');
    } finally {
      setTwoFASaving(false);
    }
  };

  const handleSiteNestedChange = (subsection: 'contact' | 'geo', field: string, value: string | boolean) => {
    if (!settings) return;
    const sub = (settings.site as any)[subsection] ?? {};
    updateSettings({
      ...settings,
      site: { ...settings.site, [subsection]: { ...sub, [field]: value } }
    });
  };

  const handleSocialMediaApiNestedChange = (
    subsection: 'pixabay' | 'llm' | 'imageGen' | 'meta',
    field: string,
    value: string
  ) => {
    handleSocialMediaApiNestedPatch(subsection, { [field]: value });
  };

  // Aplica múltiplos campos de uma subseção em uma única atualização de estado.
  // Duas chamadas separadas de handleSocialMediaApiNestedChange em sequência (ex.: trocar
  // provider e model juntos) partem do mesmo `settings` (closure) e a segunda sobrescreve
  // a primeira, pois o setState ainda não foi aplicado entre as chamadas.
  const handleSocialMediaApiNestedPatch = (
    subsection: 'pixabay' | 'llm' | 'imageGen' | 'meta',
    patch: Record<string, string>
  ) => {
    if (!settings) return;
    const current = (settings as any).socialMediaApi ?? {};
    const sub = current[subsection] ?? {};
    updateSettings({
      ...settings,
      socialMediaApi: { ...current, [subsection]: { ...sub, ...patch } }
    } as any);
  };

  const handleAiProviderApiKeyChange = (
    provider: 'anthropic' | 'openai' | 'gemini',
    value: string
  ) => {
    if (!settings) return;
    const current = (settings as any).socialMediaApi ?? {};
    const providers = current.providers ?? {};
    updateSettings({
      ...settings,
      socialMediaApi: {
        ...current,
        providers: { ...providers, [provider]: { ...providers[provider], apiKey: value } }
      }
    } as any);
  };

  const handleUploadLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const form = new FormData();
      form.append('logo', file);
      const res = await axios.post('/settings/upload-logo', form);
      if (res.data?.url) {
        handleInputChange('site', 'logo', res.data.url);
        notifications.showSuccess('Logo enviado', 'Pré-visualização atualizada. Clique em Salvar para guardar no servidor.');
      }
    } catch (err: any) {
      notifications.showError('Erro', err.response?.data?.message || 'Falha no upload do logo');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleUploadFavicon = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFavicon(true);
    try {
      const form = new FormData();
      form.append('favicon', file);
      const res = await axios.post('/settings/upload-favicon', form);
      if (res.data?.url) {
        handleInputChange('site', 'favicon', res.data.url);
        notifications.showSuccess('Favicon enviado', 'Pré-visualização atualizada. Clique em Salvar para guardar no servidor.');
      }
    } catch (err: any) {
      notifications.showError('Erro', err.response?.data?.message || 'Falha no upload do favicon');
    } finally {
      setUploadingFavicon(false);
      e.target.value = '';
    }
  };

  const tabs = [
    { id: 'site', name: 'Site', icon: Globe },
    { id: 'email', name: 'Email', icon: Mail },
    { id: 'database', name: 'Banco de Dados', icon: Database },
    { id: 'security', name: 'Segurança', icon: Shield },
    { id: 'notifications', name: 'Notificações', icon: Bell },
    { id: 'social', name: 'Redes Sociais', icon: Globe },
    { id: 'socialMediaApi', name: 'Redes Sociais (Integrações)', icon: Share2 },
    { id: 'appearance', name: 'Aparência', icon: Palette },
    { id: 'team', name: 'Equipe', icon: Users },
    { id: 'chat', name: 'Chat (Site)', icon: MessageCircle },
    { id: 'analytics', name: 'Google Analytics', icon: BarChart3 },
    { id: 'paymentOverdueEmail', name: 'Cobrança em Atraso', icon: AlertTriangle }
  ];


  const handleInputChange = (section: keyof SystemSettings, field: string, value: any) => {
    console.log('🔍 handleInputChange chamado:', { section, field, value, settings: !!settings });
    
    if (!settings) {
      console.warn('❌ Settings não carregado');
      return;
    }
    
    // Usar type assertion mais específica para evitar problemas de tipo
    const updatedSettings = {
      ...settings,
      [section]: {
        ...(settings as any)[section],
        [field]: value
      }
    } as SystemSettings;
    
    console.log('📝 Settings atualizados:', updatedSettings);
    
    // Campo nome do site - apenas atualizar o estado, sem atualização em tempo real
    if (section === 'site' && field === 'name') {
      console.log('🎯 Campo nome do site detectado, valor:', value);
      console.log('ℹ️ Atualização em tempo real desabilitada - mudanças serão aplicadas após salvar');
      // Apenas atualizar o estado sem aplicar configurações de aparência
      updateSettingsOnly(updatedSettings);
      return;
    }
    
    updateSettings(updatedSettings);
    console.log('✅ handleInputChange concluído');
  };

  const updateAllOccurrencesOfNEIA = (newSiteName: string) => {
    console.log('🔄 updateAllOccurrencesOfNEIA chamado com:', newSiteName);
    
    if (!settings) {
      console.warn('❌ Settings não carregado');
      return;
    }
    
    // Se o nome estiver vazio, não fazer substituições
    if (!newSiteName || newSiteName.trim() === '') {
      console.log('⚠️ Nome do site vazio, pulando substituições para evitar problemas');
      return;
    }
    
    // Proteção adicional: verificar se o nome é válido
    if (newSiteName.length < 1 || newSiteName.length > 100) {
      console.warn('⚠️ Nome do site inválido (muito curto ou muito longo)');
      return;
    }
    
    // Se for apenas um caractere, fazer substituição mais cuidadosa
    if (newSiteName.length === 1) {
      console.log('⚠️ Nome muito curto, fazendo substituição cuidadosa');
      // Para caracteres únicos, substituir apenas se for diferente de "N"
      if (newSiteName === 'N') {
        console.log('⚠️ Caractere "N" detectado, restaurando "NEIA"');
        newSiteName = 'NEIA';
      }
    }
    
    try {
      console.log('🔄 Iniciando atualização de NEIA para:', newSiteName);
      
      // 1. Atualizar título da página (seguro)
      try {
        if (document.title.includes('NEIA')) {
          document.title = document.title.replace(/NEIA/g, newSiteName);
          console.log('✅ Título da página atualizado');
        }
      } catch (titleError) {
        console.warn('⚠️ Erro ao atualizar título:', titleError);
      }
      
      // 2. DESABILITADO: Manipulação de meta tags pode causar problemas
      // As meta tags serão atualizadas via React Context, não via DOM direto
      console.log('⚠️ Manipulação de meta tags desabilitada para evitar quebra do React');
      
      // 3. Atualizar localStorage se existir
      try {
        const storedSettings = localStorage.getItem('neia-settings');
        if (storedSettings && storedSettings.includes('NEIA')) {
          const parsedSettings = JSON.parse(storedSettings);
          const updatedStoredSettings = JSON.stringify(parsedSettings).replace(/NEIA/g, newSiteName);
          localStorage.setItem('neia-settings', updatedStoredSettings);
          console.log('✅ localStorage atualizado');
        }
      } catch (localStorageError) {
        console.warn('Erro ao atualizar localStorage:', localStorageError);
      }
      
      // 4. Atualizar configurações de email se contiver "NEIA"
      const updatedSettings = { ...settings };
      
      if (settings.email.fromName.includes('NEIA')) {
        updatedSettings.email = {
          ...settings.email,
          fromName: settings.email.fromName.replace(/NEIA/g, newSiteName)
        };
        console.log('✅ Email fromName atualizado');
      }
      
      // 5. Atualizar CSS personalizado se contiver "NEIA"
      if (settings.appearance.customCSS.includes('NEIA')) {
        updatedSettings.appearance = {
          ...settings.appearance,
          customCSS: settings.appearance.customCSS.replace(/NEIA/g, newSiteName)
        };
        console.log('✅ CSS personalizado atualizado');
      }
      
      // 6. Atualizar descrição do site se contiver "NEIA"
      if (settings.site.description.includes('NEIA')) {
        updatedSettings.site = {
          ...settings.site,
          description: settings.site.description.replace(/NEIA/g, newSiteName)
        };
        console.log('✅ Descrição do site atualizada');
      }
      
      updateSettings(updatedSettings);
      
      // 7. Atualizar elementos DOM específicos (seguro)
      try {
        // Apenas elementos específicos que sabemos que são seguros
        const safeSelectors = [
          'h1:not([data-react-component])',
          'h2:not([data-react-component])', 
          'h3:not([data-react-component])',
          '.footer h1, .footer h2, .footer h3',
          '.header h1, .header h2, .header h3'
        ];
        
        let updatedElements = 0;
        
        safeSelectors.forEach(selector => {
          try {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
              if (element && element.textContent && element.textContent.includes('NEIA')) {
                const originalText = element.textContent;
                element.textContent = element.textContent.replace(/NEIA/g, newSiteName);
                if (element.textContent !== originalText) {
                  updatedElements++;
                }
              }
            });
          } catch (selectorError) {
            console.warn(`⚠️ Erro com seletor ${selector}:`, selectorError);
          }
        });
        
        if (updatedElements > 0) {
          console.log(`✅ ${updatedElements} elementos DOM seguros atualizados`);
        }
      } catch (domError) {
        console.error('❌ Erro ao atualizar elementos DOM:', domError);
      }
      
      // 8. Mostrar notificação de sucesso (apenas se não houver erros)
      try {
        notifications.success.settingsUpdated();
      } catch (notificationError) {
        console.warn('⚠️ Erro ao mostrar notificação:', notificationError);
      }
      
      // 9. Log da atividade
      logSettingsActivity(
        'site_name_updated',
        `Nome do site alterado de "NEIA" para "${newSiteName}"`,
        { 
          oldName: 'NEIA', 
          newName: newSiteName,
          timestamp: new Date().toISOString()
        }
      );
      
      console.log('🎉 Atualização de NEIA concluída com sucesso!');
      
    } catch (error) {
      console.error('❌ Erro ao atualizar ocorrências de NEIA:', error);
      notifications.error.settingsUpdate('Erro ao atualizar nome no sistema');
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    
    setLoading(true);
    try {
      const response = await axios.put('/settings', settings);
      console.log('✅ Configurações salvas com sucesso!');
      
      // Registrar atividade
      logSettingsActivity(
        'settings_updated',
        `Configurações da seção ${activeTab} atualizadas`,
        { section: activeTab, timestamp: new Date().toISOString() }
      );
      
      notifications.success.settingsUpdated();
      
      // Aplicar mudanças do nome do site após salvar com sucesso
      if (settings.site.name && settings.site.name !== 'NEIA') {
        console.log('🔄 Aplicando mudanças do nome do site após salvar:', settings.site.name);
        updateAllOccurrencesOfNEIA(settings.site.name);
      }
    } catch (error: any) {
      console.error('❌ Erro ao salvar configurações:', error);
      console.error('❌ Response:', error.response);
      console.error('❌ Status:', error.response?.status);
      console.error('❌ Data:', error.response?.data);
      notifications.error.settingsUpdate(error.response?.data?.message || 'Erro ao salvar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleBackup = async () => {
    try {
      await axios.post('/settings/backup', {});
      
      // Registrar atividade
      logSettingsActivity(
        'backup_created',
        'Backup manual do banco de dados criado',
        { type: 'manual', timestamp: new Date().toISOString() }
      );
      
      notifications.success.settingsUpdated();
    } catch (error) {
      notifications.error.settingsUpdate('Erro ao criar backup do banco de dados');
    }
  };

  const renderSiteSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Nome do Site
          <span className="ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded">
            🔄 Atualiza todo o sistema
          </span>
        </label>
        <input
          type="text"
          value={safeSettings.site.name}
          onChange={(e) => handleInputChange('site', 'name', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Digite o nome do site"
        />
        <p className="mt-1 text-xs text-gray-500">
          Esta mudança substituirá todas as ocorrências de "NEIA" no sistema
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Descrição
        </label>
        <textarea
          value={safeSettings.site.description}
          onChange={(e) => handleInputChange('site', 'description', e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <div className="space-y-4">
        <p className="text-sm text-gray-600">Logo e favicon: use uma URL ou envie um arquivo (PNG, JPG, GIF, WebP, ICO, até 2 MB).</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
            <input
              type="url"
              value={safeSettings.site.logo}
              onChange={(e) => handleInputChange('site', 'logo', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-2"
              placeholder="URL do logo"
            />
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">ou</span>
              <label className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm">
                <Upload className="w-4 h-4" />
                {uploadingLogo ? 'Enviando...' : 'Enviar arquivo'}
                <input type="file" accept="image/*" className="hidden" onChange={handleUploadLogo} disabled={uploadingLogo} />
              </label>
            </div>
            {safeSettings.site.logo?.trim() && (
              <div className="mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                <p className="text-xs text-gray-500 mb-2">Pré-visualização</p>
                <img
                  src={getAssetUrl(safeSettings.site.logo)}
                  alt="Logo do site"
                  className="max-h-16 max-w-full object-contain"
                />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Favicon</label>
            <input
              type="url"
              value={safeSettings.site.favicon}
              onChange={(e) => handleInputChange('site', 'favicon', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-2"
              placeholder="URL do favicon"
            />
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">ou</span>
              <label className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm">
                <Upload className="w-4 h-4" />
                {uploadingFavicon ? 'Enviando...' : 'Enviar arquivo'}
                <input type="file" accept="image/*,.ico" className="hidden" onChange={handleUploadFavicon} disabled={uploadingFavicon} />
              </label>
            </div>
            {safeSettings.site.favicon?.trim() && (
              <div className="mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                <p className="text-xs text-gray-500 mb-2">Pré-visualização</p>
                <img
                  src={getAssetUrl(safeSettings.site.favicon)}
                  alt="Favicon do site"
                  className="h-8 w-8 object-contain"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t pt-6 mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Informações de contato do site
        </h3>
        <p className="text-sm text-gray-600 mb-4">Exibidas no rodapé e em páginas de contato. Troca rápida para o usuário.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={safeSettings.site.contact?.email ?? ''}
              onChange={(e) => handleSiteNestedChange('contact', 'email', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="contato@exemplo.com"
            />
          </div>
          <div>
            <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-2"><Phone className="w-4 h-4" /> Telefone</label>
            <input
              type="text"
              value={safeSettings.site.contact?.phone ?? ''}
              onChange={(e) => handleSiteNestedChange('contact', 'phone', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="+55 11 99999-9999"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Endereço</label>
            <input
              type="text"
              value={safeSettings.site.contact?.address ?? ''}
              onChange={(e) => handleSiteNestedChange('contact', 'address', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Rua, número, bairro, cidade, país"
            />
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-2"><Clock className="w-4 h-4" /> Horário de funcionamento</label>
            <input
              type="text"
              value={safeSettings.site.contact?.businessHours ?? ''}
              onChange={(e) => handleSiteNestedChange('contact', 'businessHours', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Seg–Sex 9h–18h | Sáb 9h–13h"
            />
          </div>
        </div>
      </div>

      <div className="border-t pt-6 mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5" />
          Localização (geo)
        </h3>
        <p className="text-sm text-gray-600 mb-4">Latitude e longitude para mapas e SEO. Ex.: Google Maps, schema.org.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
            <input
              type="text"
              value={safeSettings.site.geo?.latitude ?? ''}
              onChange={(e) => handleSiteNestedChange('geo', 'latitude', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="-23.5505"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
            <input
              type="text"
              value={safeSettings.site.geo?.longitude ?? ''}
              onChange={(e) => handleSiteNestedChange('geo', 'longitude', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="-46.6333"
            />
          </div>
          {(safeSettings.site.geo?.latitude && safeSettings.site.geo?.longitude) && (
            <div className="md:col-span-2">
              <a
                href={`https://www.google.com/maps?q=${encodeURIComponent(safeSettings.site.geo.latitude + ',' + safeSettings.site.geo.longitude)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary-600 hover:underline"
              >
                Abrir no Google Maps →
              </a>
            </div>
          )}
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={safeSettings.site.geo?.showLocation !== false}
                onChange={(e) => handleSiteNestedChange('geo', 'showLocation', e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Exibir seção "Nossa Localização" na página de Contato
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">Desmarque se o cliente não quiser divulgar o endereço/mapa publicamente.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Idioma
          </label>
          <select
            value={safeSettings.site.language}
            onChange={(e) => handleInputChange('site', 'language', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="pt-BR">Português (Brasil)</option>
            <option value="en-US">English (US)</option>
            <option value="es-ES">Español</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Fuso Horário
          </label>
          <select
            value={safeSettings.site.timezone}
            onChange={(e) => handleInputChange('site', 'timezone', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="America/Sao_Paulo">São Paulo (GMT-3)</option>
            <option value="America/New_York">New York (GMT-5)</option>
            <option value="Europe/London">London (GMT+0)</option>
          </select>
        </div>
      </div>
    </div>
  );

  const email = (settings as SystemSettings | null)?.email ?? ({} as SystemSettings['email']);
  const provider = email.provider ?? '';

  const renderEmailSettings = () => (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Escolha o provedor de e-mail para convites, reset de senha e notificações. Se deixar &quot;Nenhum (usa .env)&quot;, o servidor usará variáveis de ambiente.
      </p>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Provedor de e-mail</label>
        <select
          value={provider}
          onChange={(e) => handleInputChange('email', 'provider', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">Nenhum (usa .env do servidor)</option>
          <option value="smtp">SMTP (provedor / servidor próprio)</option>
          <option value="sendgrid">SendGrid</option>
          <option value="mailersend">MailerSend</option>
        </select>
      </div>

      {provider === 'smtp' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
              <input
                type="text"
                value={email.smtpHost ?? ''}
                onChange={(e) => handleInputChange('email', 'smtpHost', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="smtp.seudominio.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Porta</label>
              <input
                type="number"
                value={email.smtpPort ?? 587}
                onChange={(e) => handleInputChange('email', 'smtpPort', parseInt(e.target.value) || 587)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Usuário SMTP</label>
              <input
                type="text"
                value={email.smtpUser ?? ''}
                onChange={(e) => handleInputChange('email', 'smtpUser', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Senha SMTP</label>
              <input
                type="password"
                value={email.smtpPassword ?? ''}
                onChange={(e) => handleInputChange('email', 'smtpPassword', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Deixe em branco para não alterar"
                autoComplete="new-password"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="smtpSecure"
              checked={!!email.smtpSecure}
              onChange={(e) => handleInputChange('email', 'smtpSecure', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="smtpSecure" className="text-sm text-gray-700">Conexão segura (TLS/SSL, porta 465)</label>
          </div>
        </>
      )}

      {provider === 'sendgrid' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SendGrid API Key</label>
          <input
            type="password"
            value={email.sendgridApiKey ?? ''}
            onChange={(e) => handleInputChange('email', 'sendgridApiKey', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="SG.xxx (deixe em branco para não alterar)"
            autoComplete="new-password"
          />
        </div>
      )}

      {provider === 'mailersend' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">MailerSend API Key</label>
          <input
            type="password"
            value={email.mailersendApiKey ?? ''}
            onChange={(e) => handleInputChange('email', 'mailersendApiKey', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="mlsn.xxx (deixe em branco para não alterar)"
            autoComplete="new-password"
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-gray-200 pt-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">E-mail remetente</label>
          <input
            type="email"
            value={email.fromEmail ?? ''}
            onChange={(e) => handleInputChange('email', 'fromEmail', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="noreply@seudominio.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Nome remetente</label>
          <input
            type="text"
            value={email.fromName ?? ''}
            onChange={(e) => handleInputChange('email', 'fromName', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Nome do site"
          />
        </div>
      </div>
    </div>
  );

  const renderDatabaseSettings = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-blue-50 rounded-lg">
        <div>
          <h3 className="text-lg font-semibold text-blue-900">Backup do Banco de Dados</h3>
          <p className="text-blue-700">Gerencie os backups automáticos do sistema</p>
        </div>
        <button
          onClick={handleBackup}
          className="btn-primary flex items-center justify-center space-x-2"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Criar Backup Agora</span>
        </button>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm font-medium text-gray-700">
            Backup Automático
          </label>
          <p className="text-sm text-gray-500">Criar backups automaticamente</p>
        </div>
        <input
          type="checkbox"
          checked={safeSettings.database.backupEnabled}
          onChange={(e) => handleInputChange('database', 'backupEnabled', e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Frequência do Backup
        </label>
        <select
          value={safeSettings.database.backupFrequency}
          onChange={(e) => handleInputChange('database', 'backupFrequency', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="daily">Diário</option>
          <option value="weekly">Semanal</option>
          <option value="monthly">Mensal</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Dias de Retenção
        </label>
        <input
          type="number"
          value={safeSettings.database.retentionDays}
          onChange={(e) => handleInputChange('database', 'retentionDays', parseInt(e.target.value))}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Timeout da Sessão (horas)
        </label>
        <input
          type="number"
          value={safeSettings.security.sessionTimeout}
          onChange={(e) => handleInputChange('security', 'sessionTimeout', parseInt(e.target.value))}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Máximo de Tentativas de Login
        </label>
        <input
          type="number"
          value={safeSettings.security.maxLoginAttempts}
          onChange={(e) => handleInputChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tamanho Mínimo da Senha
        </label>
        <input
          type="number"
          value={safeSettings.security.passwordMinLength}
          onChange={(e) => handleInputChange('security', 'passwordMinLength', parseInt(e.target.value))}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <div className="border-t pt-6 mt-6">
        <h3 className="text-base font-semibold text-gray-900 mb-1">Autenticação em duas etapas (2FA)</h3>
        <p className="text-sm text-gray-500 mb-4">
          Protege a conta <span className="font-medium">{user?.email}</span> exigindo um código do seu app
          autenticador (Google Authenticator, Authy, etc.) a cada login.
        </p>

        {twoFALoading ? (
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
        ) : twoFARecoveryCodes ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm font-medium text-amber-900 mb-2">
              2FA ativado! Guarde estes códigos de recuperação — cada um só pode ser usado uma vez, caso perca acesso ao app autenticador:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 font-mono text-sm bg-white border border-amber-200 rounded-lg p-3 mb-3">
              {twoFARecoveryCodes.map((code) => (
                <span key={code}>{code}</span>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setTwoFARecoveryCodes(null)}
              className="btn-primary"
            >
              Já salvei os códigos
            </button>
          </div>
        ) : twoFASetupData ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Escaneie o QR code abaixo no seu app autenticador e digite o código de 6 dígitos gerado:
            </p>
            <img src={twoFASetupData.qrCodeDataUrl} alt="QR code 2FA" className="w-48 h-48 border rounded-lg" />
            <p className="text-xs text-gray-500">
              Não consegue escanear? Digite manualmente: <span className="font-mono">{twoFASetupData.secret}</span>
            </p>
            <div className="flex items-center gap-3">
              <input
                type="text"
                inputMode="numeric"
                value={twoFAConfirmCode}
                onChange={(e) => setTwoFAConfirmCode(e.target.value)}
                placeholder="000000"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-center tracking-widest focus:ring-2 focus:ring-primary-500"
              />
              <button
                type="button"
                onClick={confirmTwoFASetup}
                disabled={twoFASaving || twoFAConfirmCode.length < 6}
                className="btn-primary disabled:opacity-50"
              >
                Confirmar e ativar
              </button>
              <button
                type="button"
                onClick={() => setTwoFASetupData(null)}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : twoFAEnabled ? (
          <div className="flex items-center justify-between">
            <span className="px-3 py-1 text-sm rounded-full bg-green-100 text-green-700 font-medium">
              2FA ativado
            </span>
            <button
              type="button"
              onClick={() => setShowDisableTwoFAModal(true)}
              className="px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors duration-200"
            >
              Desativar 2FA
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={startTwoFASetup}
            disabled={twoFASaving}
            className="btn-primary disabled:opacity-50"
          >
            Ativar 2FA
          </button>
        )}
      </div>

      {showDisableTwoFAModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full p-6 transition-all duration-200 ${
            isTwoFAModalExpanded ? 'max-w-[95vw] max-h-[95vh] overflow-y-auto' : 'sm:max-w-sm'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Desativar 2FA</h3>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={toggleTwoFAModalExpanded}
                  className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title={isTwoFAModalExpanded ? 'Restaurar' : 'Expandir'}
                >
                  {isTwoFAModalExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowDisableTwoFAModal(false); setDisableTwoFAPassword(''); setIsTwoFAModalExpanded(false); }}
                  className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title="Fechar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">Digite sua senha atual para confirmar.</p>
            <input
              type="password"
              value={disableTwoFAPassword}
              onChange={(e) => setDisableTwoFAPassword(e.target.value)}
              placeholder="Senha"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-primary-500"
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowDisableTwoFAModal(false); setDisableTwoFAPassword(''); setIsTwoFAModalExpanded(false); }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={disableTwoFA}
                disabled={twoFASaving || !disableTwoFAPassword}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 disabled:opacity-50"
              >
                Desativar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm font-medium text-gray-700">
            Notificações por Email
          </label>
          <p className="text-sm text-gray-500">Enviar notificações por email</p>
        </div>
        <input
          type="checkbox"
          checked={safeSettings.notifications.emailNotifications}
          onChange={(e) => handleInputChange('notifications', 'emailNotifications', e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm font-medium text-gray-700">
            Notificações para Administradores
          </label>
          <p className="text-sm text-gray-500">Alertas para administradores</p>
        </div>
        <input
          type="checkbox"
          checked={safeSettings.notifications.adminNotifications}
          onChange={(e) => handleInputChange('notifications', 'adminNotifications', e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm font-medium text-gray-700">
            Notificações para Usuários
          </label>
          <p className="text-sm text-gray-500">Alertas para usuários</p>
        </div>
        <input
          type="checkbox"
          checked={safeSettings.notifications.userNotifications}
          onChange={(e) => handleInputChange('notifications', 'userNotifications', e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
      </div>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cor Primária
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="color"
              value={safeSettings.appearance.primaryColor}
              onChange={(e) => handleInputChange('appearance', 'primaryColor', e.target.value)}
              className="w-12 h-12 border border-gray-300 rounded-lg"
            />
            <input
              type="text"
              value={safeSettings.appearance.primaryColor}
              onChange={(e) => handleInputChange('appearance', 'primaryColor', e.target.value)}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cor Secundária
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="color"
              value={safeSettings.appearance.secondaryColor}
              onChange={(e) => handleInputChange('appearance', 'secondaryColor', e.target.value)}
              className="w-12 h-12 border border-gray-300 rounded-lg"
            />
            <input
              type="text"
              value={safeSettings.appearance.secondaryColor}
              onChange={(e) => handleInputChange('appearance', 'secondaryColor', e.target.value)}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tema
        </label>
        <select
          value={safeSettings.appearance.theme}
          onChange={(e) => handleInputChange('appearance', 'theme', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="light">Claro</option>
          <option value="dark">Escuro</option>
          <option value="auto">Automático</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          CSS Personalizado
        </label>
        <textarea
          value={safeSettings.appearance.customCSS}
          onChange={(e) => handleInputChange('appearance', 'customCSS', e.target.value)}
          rows={8}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
          placeholder="/* CSS personalizado */"
        />
      </div>
    </div>
  );

  const renderSocialSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/sua-pagina' },
          { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/seu-perfil' },
          { key: 'twitter', label: 'Twitter/X', placeholder: 'https://twitter.com/seu-perfil' },
          { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/sua-empresa' },
          { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@seucanal' },
          { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@seuusuario' },
          { key: 'whatsapp', label: 'WhatsApp', placeholder: 'https://wa.me/5511999999999' },
        ].map((item) => (
          <div key={item.key}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {item.label}
            </label>
            <input
              type="url"
              value={(safeSettings.social as any)?.[item.key] || ''}
              onChange={(e) => handleInputChange('social' as any, item.key, e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder={item.placeholder}
            />
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500">Preencha apenas as redes que deseja exibir no site.</p>
    </div>
  );

  const AI_PROVIDER_MODEL_DEFAULTS: Record<string, Record<string, string>> = {
    llm: { anthropic: 'claude-sonnet-4-5', openai: 'gpt-4o', gemini: 'gemini-2.0-flash' },
    imageGen: { openai: 'gpt-image-1', gemini: 'imagen-3.0-generate-002' }
  };

  const renderSocialMediaApiSettings = () => {
    const socialMediaApi = safeSettings.socialMediaApi ?? {};
    const pixabaySettings = socialMediaApi.pixabay ?? {};
    const providerSettings = socialMediaApi.providers ?? {};
    const llmSettings = socialMediaApi.llm ?? {};
    const imageGenSettings = socialMediaApi.imageGen ?? {};
    const metaSettings = socialMediaApi.meta ?? {};

    return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Chaves de API usadas pelo módulo Redes Sociais para gerar descrição/hashtags (IA), gerar/buscar imagens (Pixabay/IA) e publicar no Instagram/Facebook.
        Sem as credenciais do Meta configuradas, a publicação nessas redes roda em modo simulado (registrada, mas não enviada de verdade).
      </p>

      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Pixabay</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Pixabay API Key</label>
          <input
            type="password"
            value={pixabaySettings.apiKey ?? ''}
            onChange={(e) => handleSocialMediaApiNestedChange('pixabay', 'apiKey', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="Deixe em branco para não alterar"
            autoComplete="new-password"
          />
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Provedores de IA</h3>
        <p className="text-sm text-gray-600 mb-4">
          Cadastre a chave de cada provedor que for usar. As chaves aqui são compartilhadas entre a geração de texto e a geração de imagem por IA abaixo.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Anthropic (Claude) API Key</label>
            <input
              type="password"
              value={providerSettings.anthropic?.apiKey ?? ''}
              onChange={(e) => handleAiProviderApiKeyChange('anthropic', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Deixe em branco para não alterar"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">OpenAI API Key</label>
            <input
              type="password"
              value={providerSettings.openai?.apiKey ?? ''}
              onChange={(e) => handleAiProviderApiKeyChange('openai', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Deixe em branco para não alterar"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Google Gemini API Key</label>
            <input
              type="password"
              value={providerSettings.gemini?.apiKey ?? ''}
              onChange={(e) => handleAiProviderApiKeyChange('gemini', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Deixe em branco para não alterar"
              autoComplete="new-password"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Geração de texto (IA)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Provedor</label>
            <select
              value={llmSettings.provider ?? 'anthropic'}
              onChange={(e) => {
                const provider = e.target.value;
                handleSocialMediaApiNestedPatch('llm', { provider, model: AI_PROVIDER_MODEL_DEFAULTS.llm[provider] ?? '' });
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="anthropic">Anthropic (Claude)</option>
              <option value="openai">OpenAI (GPT)</option>
              <option value="gemini">Google (Gemini)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Modelo</label>
            <input
              type="text"
              value={llmSettings.model ?? AI_PROVIDER_MODEL_DEFAULTS.llm[llmSettings.provider ?? 'anthropic']}
              onChange={(e) => handleSocialMediaApiNestedChange('llm', 'model', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Geração de imagem por IA</h3>
        <p className="text-sm text-gray-600 mb-4">
          Caminho opcional/alternativo ao Pixabay: gera uma imagem original a partir de um prompt.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Provedor</label>
            <select
              value={imageGenSettings.provider ?? 'openai'}
              onChange={(e) => {
                const provider = e.target.value;
                handleSocialMediaApiNestedPatch('imageGen', { provider, model: AI_PROVIDER_MODEL_DEFAULTS.imageGen[provider] ?? '' });
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="openai">OpenAI (gpt-image / DALL·E)</option>
              <option value="gemini">Google (Imagen)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Modelo</label>
            <input
              type="text"
              value={imageGenSettings.model ?? AI_PROVIDER_MODEL_DEFAULTS.imageGen[imageGenSettings.provider ?? 'openai']}
              onChange={(e) => handleSocialMediaApiNestedChange('imageGen', 'model', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Meta (Instagram / Facebook)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">App ID</label>
            <input
              type="text"
              value={metaSettings.appId ?? ''}
              onChange={(e) => handleSocialMediaApiNestedChange('meta', 'appId', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">App Secret</label>
            <input
              type="password"
              value={metaSettings.appSecret ?? ''}
              onChange={(e) => handleSocialMediaApiNestedChange('meta', 'appSecret', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Deixe em branco para não alterar"
              autoComplete="new-password"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Page Access Token</label>
            <input
              type="password"
              value={metaSettings.pageAccessToken ?? ''}
              onChange={(e) => handleSocialMediaApiNestedChange('meta', 'pageAccessToken', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Deixe em branco para não alterar"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Instagram Business Account ID</label>
            <input
              type="text"
              value={metaSettings.instagramBusinessAccountId ?? ''}
              onChange={(e) => handleSocialMediaApiNestedChange('meta', 'instagramBusinessAccountId', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Facebook Page ID</label>
            <input
              type="text"
              value={metaSettings.facebookPageId ?? ''}
              onChange={(e) => handleSocialMediaApiNestedChange('meta', 'facebookPageId', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderTeamSettings = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Configurações da Seção Equipe</h3>
        <p className="text-blue-700 text-sm">
          Configure como a seção "Nossa Equipe" será exibida na página "Sobre".
        </p>
      </div>

      <div className="space-y-6">
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <h4 className="text-lg font-medium text-gray-900">Exibir Seção da Equipe</h4>
            <p className="text-sm text-gray-600">
              Mostra a seção "Nossa Equipe" na página "Sobre"
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={safeSettings.team.showTeamSection}
              onChange={(e) => handleInputChange('team', 'showTeamSection', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <h4 className="text-lg font-medium text-gray-900">Ocultar Quando Vazia</h4>
            <p className="text-sm text-gray-600">
              Oculta a seção automaticamente quando não há membros da equipe cadastrados
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={safeSettings.team.hideWhenEmpty}
              onChange={(e) => handleInputChange('team', 'hideWhenEmpty', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-yellow-800 mb-2">💡 Dica</h4>
        <p className="text-sm text-yellow-700">
          Para gerenciar os membros da equipe, acesse <strong>Dashboard → Nossa Equipe</strong>.
          As configurações aqui controlam apenas a exibição da seção na página "Sobre".
        </p>
      </div>
    </div>
  );

  const chatWidget = settings?.chatWidget ?? { enabled: false, webhookUrl: '', typingLabel: 'IA digitando...', title: 'Atendimento NEIA' };
  const analytics = settings?.analytics ?? { enabled: false, googleAnalyticsId: '', metaPixelId: '' };

  const paymentOverdueEmail = settings?.paymentOverdueEmail ?? {
    enabled: false,
    client: { subject: '', body: '' },
    admin: { subject: '', body: '' }
  };

  const handlePaymentOverdueEmailNestedChange = (
    subsection: 'client' | 'admin',
    field: 'subject' | 'body',
    value: string
  ) => {
    if (!settings) return;
    const current = settings.paymentOverdueEmail ?? {
      enabled: false,
      client: { subject: '', body: '' },
      admin: { subject: '', body: '' }
    };
    const sub = current[subsection] ?? { subject: '', body: '' };
    updateSettings({
      ...settings,
      paymentOverdueEmail: { ...current, [subsection]: { ...sub, [field]: value } }
    } as any);
  };

  const PAYMENT_OVERDUE_VARIABLES = [
    { token: '{{clientName}}', desc: 'Nome do cliente' },
    { token: '{{valor}}', desc: 'Valor da parcela' },
    { token: '{{vencimento}}', desc: 'Data de vencimento' },
    { token: '{{numeroParcela}}', desc: 'Número da parcela' },
    { token: '{{totalParcelas}}', desc: 'Total de parcelas' },
    { token: '{{descricaoVenda}}', desc: 'Descrição da venda' },
    { token: '{{diasAtraso}}', desc: 'Dias em atraso' },
    { token: '{{clientEmail}}', desc: 'Email do cliente (só admin)' },
    { token: '{{adminUrl}}', desc: 'Link do painel (só admin)' }
  ];

  const renderPaymentOverdueEmailSettings = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Email de Cobrança em Atraso</h3>
        <p className="text-blue-700 text-sm">
          Quando uma parcela vence sem pagamento, o sistema (verificação horária) envia automaticamente
          um email ao cliente e à equipe administrativa. Deixe assunto/corpo em branco para usar o texto
          padrão do sistema.
        </p>
      </div>

      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div>
          <h4 className="text-lg font-medium text-gray-900">Ativar envio automático</h4>
          <p className="text-sm text-gray-600">
            Desative para apenas marcar a parcela como atrasada, sem enviar email.
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={paymentOverdueEmail.enabled}
            onChange={(e) => handleInputChange('paymentOverdueEmail' as any, 'enabled', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-yellow-800 mb-2">Variáveis disponíveis</h4>
        <div className="flex flex-wrap gap-2 text-xs">
          {PAYMENT_OVERDUE_VARIABLES.map((v) => (
            <span
              key={v.token}
              title={v.desc}
              className="px-2 py-1 bg-white border border-yellow-300 rounded font-mono"
            >
              {v.token}
            </span>
          ))}
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <h4 className="font-medium text-gray-900">Email para o Cliente</h4>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Assunto</label>
          <input
            type="text"
            value={paymentOverdueEmail.client.subject}
            onChange={(e) => handlePaymentOverdueEmailNestedChange('client', 'subject', e.target.value)}
            placeholder="[NEIA] Pagamento em atraso — venceu em {{vencimento}}"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Corpo</label>
          <textarea
            rows={8}
            value={paymentOverdueEmail.client.body}
            onChange={(e) => handlePaymentOverdueEmailNestedChange('client', 'body', e.target.value)}
            placeholder={'Olá {{clientName}},\n\nA parcela {{numeroParcela}}/{{totalParcelas}} no valor de {{valor}} venceu em {{vencimento}} e está em atraso há {{diasAtraso}} dia(s).'}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <h4 className="font-medium text-gray-900">Email para a Equipe (Admin)</h4>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Assunto</label>
          <input
            type="text"
            value={paymentOverdueEmail.admin.subject}
            onChange={(e) => handlePaymentOverdueEmailNestedChange('admin', 'subject', e.target.value)}
            placeholder="[NEIA] Cobrança em atraso — {{clientName}}"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Corpo</label>
          <textarea
            rows={8}
            value={paymentOverdueEmail.admin.body}
            onChange={(e) => handlePaymentOverdueEmailNestedChange('admin', 'body', e.target.value)}
            placeholder={'O cliente {{clientName}} ({{clientEmail}}) tem a parcela {{numeroParcela}}/{{totalParcelas}} no valor de {{valor}} vencida em {{vencimento}} ({{diasAtraso}} dia(s) de atraso).\n\nPainel: {{adminUrl}}'}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>
    </div>
  );

  const renderChatWidgetSettings = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Ícone de Chat no Site</h3>
        <p className="text-blue-700 text-sm">
          Exibe um ícone de chat animado no canto da página. Ao clicar, o visitante é direcionado ao endereço configurado (ex.: webhook do n8n que abre o chat).
        </p>
      </div>

      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div>
          <h4 className="text-lg font-medium text-gray-900">Exibir ícone de chat no site</h4>
          <p className="text-sm text-gray-600">
            Ativa o botão flutuante de chat para os visitantes. Desative para ocultar.
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={chatWidget.enabled}
            onChange={(e) => handleInputChange('chatWidget' as any, 'enabled', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Endereço do webhook (n8n ou link do chat)
        </label>
        <input
          type="url"
          value={chatWidget.webhookUrl}
          onChange={(e) => handleInputChange('chatWidget' as any, 'webhookUrl', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="https://seu-n8n.com/webhook/... ou https://wa.me/..."
        />
        <p className="text-xs text-gray-500 mt-1">
          URL que abre ao clicar no ícone (ex.: webhook GET do n8n que redireciona para o chat, ou link do WhatsApp).
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Título do chat (cabeçalho da janela)
        </label>
        <input
          type="text"
          value={chatWidget.title ?? 'Atendimento NEIA'}
          onChange={(e) => handleInputChange('chatWidget' as any, 'title', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Atendimento NEIA"
        />
        <p className="text-xs text-gray-500 mt-1">
          Ex.: &quot;Atendimento NEIA&quot;, &quot;Fale com a Neia&quot;, &quot;Suporte&quot;
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Texto exibido enquanto a IA responde
        </label>
        <input
          type="text"
          value={chatWidget.typingLabel ?? 'IA digitando...'}
          onChange={(e) => handleInputChange('chatWidget' as any, 'typingLabel', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="IA digitando..."
        />
        <p className="text-xs text-gray-500 mt-1">
          Ex.: &quot;IA digitando...&quot;, &quot;Neia está pensando...&quot;, &quot;Aguarde...&quot;
        </p>
      </div>
    </div>
  );

  const renderAnalyticsSettings = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Google Analytics (GA4)</h3>
        <p className="text-blue-700 text-sm">
          Rastreia visitas às páginas públicas do site usando o Google Analytics 4. O painel administrativo (/admin) nunca é rastreado.
        </p>
      </div>

      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div>
          <h4 className="text-lg font-medium text-gray-900">Ativar rastreamento</h4>
          <p className="text-sm text-gray-600">
            Ativa o Google Analytics nas páginas públicas. Desative para parar de rastrear.
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={analytics.enabled}
            onChange={(e) => handleInputChange('analytics' as any, 'enabled', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          ID de Métrica do GA4 (Measurement ID)
        </label>
        <input
          type="text"
          value={analytics.googleAnalyticsId}
          onChange={(e) => handleInputChange('analytics' as any, 'googleAnalyticsId', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="G-XXXXXXXXXX"
        />
        <p className="text-xs text-gray-500 mt-1">
          Encontrado em Admin do Google Analytics → Fluxos de dados → seu fluxo web. Formato: G-XXXXXXXXXX.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Meta Pixel ID (Facebook/Instagram Ads)
        </label>
        <input
          type="text"
          value={analytics.metaPixelId}
          onChange={(e) => handleInputChange('analytics' as any, 'metaPixelId', e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="123456789012345"
        />
        <p className="text-xs text-gray-500 mt-1">
          Encontrado no Gerenciador de Eventos do Meta (Facebook Business) → Fontes de dados → seu Pixel. Usado nas
          landing pages para otimização de campanhas — só dispara depois que o visitante aceita cookies de marketing.
        </p>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'site':
        return renderSiteSettings();
      case 'email':
        return renderEmailSettings();
      case 'database':
        return renderDatabaseSettings();
      case 'security':
        return renderSecuritySettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'social':
        return renderSocialSettings();
      case 'socialMediaApi':
        return renderSocialMediaApiSettings();
      case 'appearance':
        return renderAppearanceSettings();
      case 'team':
        return renderTeamSettings();
      case 'chat':
        return renderChatWidgetSettings();
      case 'analytics':
        return renderAnalyticsSettings();
      case 'paymentOverdueEmail':
        return renderPaymentOverdueEmailSettings();
      default:
        return null;
    }
  };

  if (!settings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  // TypeScript assertion - sabemos que settings não é null aqui
  const safeSettings = settings as SystemSettings;
  
  console.log('🔍 safeSettings:', safeSettings);
  console.log('🔍 safeSettings.site.name:', safeSettings?.site?.name);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Breadcrumbs />
        </div>

        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.settings')}</h1>
              <p className="text-gray-600">Gerencie as configurações gerais do sistema</p>
            </div>
            <button
              onClick={handleSave}
              disabled={loading}
              className="btn-primary flex items-center justify-center space-x-2"
            >
              <Save className="w-5 h-5" />
              <span>{loading ? 'Salvando...' : 'Salvar Configurações'}</span>
            </button>
          </div>
        </div>


        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-100 text-primary-700 border border-primary-200'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* Main Settings Content */}
              <div className="xl:col-span-2">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    {(() => {
                      const Icon = tabs.find(tab => tab.id === activeTab)?.icon || SettingsIcon;
                      return <Icon className="w-6 h-6 text-primary-600" />;
                    })()}
                    <h2 className="text-xl font-semibold text-gray-900">
                      {tabs.find(tab => tab.id === activeTab)?.name}
                    </h2>
                  </div>
                  {renderTabContent()}
                </div>
              </div>

              {/* Activities Sidebar */}
              <div className="xl:col-span-1">
                <SystemActivities />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
